import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Download,
  AlertTriangle,
  CheckCircle,
  Calendar,
  TrendingUp,
  Users,
  PieChart,
  Clock,
  XCircle
} from 'lucide-react';
import trainingService from '../../../api/services/trainingService';
import Button from '../../../components/common/Button/Button';
import { formatIndianNumber } from '../../../utils/amountFormatter';

const formatNumber = (value) => formatIndianNumber(value || 0);

const KpiCard = ({ title, value, subtitle, icon: Icon, color = 'bg-brand' }) => {
  let bgColor = "bg-[#0F2942]";
  if (color.includes("brand")) bgColor = "bg-[#0F2942]";
  else if (color.includes("yellow")) bgColor = "bg-[#F59E0B]";
  else if (color.includes("green") || color.includes("emerald")) bgColor = "bg-[#10B981]";
  else if (color.includes("purple")) bgColor = "bg-[#8B5CF6]";
  else if (color.includes("red")) bgColor = "bg-[#EF4444]";
  else if (color.includes("blue")) bgColor = "bg-[#3B82F6]";
  
  return (
    <div className="bg-[#F8FAFC] dark:bg-slate-800/50 rounded-2xl p-5 border border-slate-200 dark:border-slate-700/50 flex items-center justify-between transition-all hover:shadow-sm">
      <div>
        <h3 className="text-[13px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
          {title}
        </h3>
        {subtitle && (
          <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-0.5">
            {subtitle}
          </p>
        )}
        <p className="text-[28px] font-bold text-slate-900 dark:text-white mt-2 leading-none">
          {value}
        </p>
      </div>
      <div className={`w-12 h-12 rounded-full ${bgColor} flex items-center justify-center flex-shrink-0 shadow-sm`}>
        <Icon className="h-5 w-5 text-white stroke-[2]" />
      </div>
    </div>
  );
};

const SectionCard = ({ title, subtitle, action, children }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
    <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
      <div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EmptyState = ({ message }) => (
  <div className="py-8 text-center text-gray-500 text-sm">{message}</div>
);

const buildCsv = (rows) => rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');

const downloadCsv = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const ReportsAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const departmentOptions = useMemo(() => {
    const departments = new Set(['all']);
    if (Array.isArray(analytics?.availableDepartments)) {
      analytics.availableDepartments.forEach((dept) => {
        if (dept && dept.trim()) {
          departments.add(dept);
        }
      });
    }
    return Array.from(departments);
  }, [analytics]);

  const summaryCards = useMemo(() => {
    const kpis = analytics?.kpis || {};
    return [
      {
        title: 'Learners',
        value: formatNumber(kpis.totalLearners),
        icon: Users,
        color: 'bg-brand'
      },
      {
        title: 'Completion Rate',
        value: `${kpis.completionRate || 0}%`,
        icon: CheckCircle,
        color: 'bg-brand'
      },
      {
        title: 'Overdue',
        value: formatNumber(kpis.overdueLearners),
        icon: AlertTriangle,
        color: 'bg-red-600'
      },
      {
        title: 'Classes (7d)',
        value: formatNumber(kpis.sessionsThisWeek),
        icon: Calendar,
        color: 'bg-indigo-600'
      },
      {
        title: 'Expiring Certifications',
        value: formatNumber(kpis.expiringCertifications),
        icon: Clock,
        color: 'bg-purple-600'
      },
      {
        title: 'Expired Certifications',
        value: formatNumber(kpis.expiredCertifications),
        icon: XCircle,
        color: 'bg-gray-600'
      }
    ];
  }, [analytics]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const analyticsRes = await trainingService.getTrainingAnalytics();
      if (!analyticsRes?.success) {
        throw new Error(analyticsRes?.error || 'Failed to load analytics');
      }
      setAnalytics(analyticsRes.data || null);
    } catch (err) {
      console.error('Learning reports & analytics error:', err);
      setError(err.message || 'Unable to load learning analytics right now');
    } finally {
      setLoading(false);
    }
  };

  const riskRows = analytics?.atRiskLearners || [];
  const upcomingSessions = analytics?.upcomingSessions || [];
  const trendSeries = analytics?.trend || [];
  const departmentPerformance = analytics?.departmentPerformance || [];
  const coursePerformance = analytics?.coursePerformance || [];

  const handleExportAnalytics = () => {
    if (!analytics) return;

    const rows = [];
    const addRows = (sectionTitle, headers, dataRows) => {
      rows.push([sectionTitle]);
      if (headers && headers.length) rows.push(headers);
      if (dataRows.length === 0) {
        rows.push(['No data']);
      } else {
        rows.push(...dataRows);
      }
      rows.push([]);
    };

    addRows('KPI Overview', ['Metric', 'Value'], Object.entries(analytics.kpis || {}).map(([key, value]) => [key, value]));
    addRows(
      'Learner Status',
      ['Status', 'Count'],
      ['completed', 'inProgress', 'notStarted', 'overdue'].map((key) => [key, analytics.learnerStatus?.[key] ?? 0])
    );
    addRows(
      'At-Risk Learners',
      ['Employee', 'Department', 'Course', 'Status', 'Days Overdue'],
      (analytics.atRiskLearners || []).map((learner) => [
        learner.employeeName,
        learner.department,
        learner.courseTitle,
        learner.status,
        learner.daysOverdue
      ])
    );
    addRows(
      'Trend (Assignments vs Completions)',
      ['Label', 'Assigned', 'Completed'],
      (analytics.trend || []).map((entry) => [entry.label, entry.assigned, entry.completed])
    );
    addRows(
      'Content Engagement',
      ['Content', 'Category', 'Learners', 'Completion %'],
      (analytics.coursePerformance || []).map((course) => [course.title, course.category, course.learners, course.completionRate])
    );
    if (analytics.quiz) {
      addRows('Assessment Performance', ['Avg Score', 'Pass Rate', 'Attempts'], [[analytics.quiz.avgScore, analytics.quiz.passRate, analytics.quiz.attempts]]);
    }
    addRows(
      'Upcoming Classes',
      ['Title', 'Content', 'Date'],
      (analytics.upcomingSessions || []).map((session) => [session.title, session.courseTitle, session.date])
    );
    addRows(
      'Department Performance',
      ['Department', 'Learners', 'Completion %'],
      (analytics.departmentPerformance || []).map((dept) => [dept.department, dept.learners, dept.completionRate])
    );

    const csvContent = buildCsv(rows);
    downloadCsv(csvContent, `learning_analytics_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const renderSummary = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {summaryCards.map((card) => (
        <KpiCard key={card.title} {...card} />
      ))}
    </div>
  );

  const renderAtRiskLearners = () => {
    const topRisks = riskRows.slice(0, 10);
    return (
      <SectionCard
        title="At-Risk Learners"
        subtitle="Focused list of overdue learners"
      >
        {topRisks.length === 0 ? (
          <EmptyState message="No learners are at risk right now." />
        ) : (
          <div className="divide-y divide-gray-100">
            {topRisks.map((row) => (
              <div key={`${row.assignmentId}-${row.employeeId}`} className="py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900 truncate max-w-[220px]" title={row.employeeName}>
                    {row.employeeName}
                  </p>
                  <p
                    className="text-xs text-gray-500 truncate max-w-[260px]"
                    title={`${row.department || 'Unassigned'} • ${row.courseTitle}`}
                  >
                    {row.department || 'Unassigned'} • {row.courseTitle}
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-600 capitalize">{row.status?.replace('_', ' ') || 'overdue'}</span>
                  <span className="text-sm font-semibold text-red-600">{row.daysOverdue}d</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    );
  };

  const renderUpcomingSessions = () => {
    const nextSessions = upcomingSessions.slice(0, 4);
    return (
      <SectionCard title="Upcoming Classes">
        {nextSessions.length === 0 ? (
          <EmptyState message="No upcoming classes scheduled." />
        ) : (
          <div className="space-y-4">
            {nextSessions.map((session) => {
              const startDate = session.date ? new Date(session.date) : null;
              const formattedDate = startDate
                ? startDate.toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true
                })
                : '--';

              return (
                <div key={session.id} className="flex items-center justify-between">
                  <div className="pr-4">
                    <p className="text-sm font-semibold text-gray-900 truncate max-w-[220px]" title={session.title}>
                      {session.title}
                    </p>
                    <p className="text-xs text-gray-500 truncate max-w-[220px]" title={session.courseTitle}>
                      {session.courseTitle}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-700">{formattedDate}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>
    );
  };

  const renderTrend = () => (
    <SectionCard
      title="Progress Trend"
      subtitle="Assignments vs completions"
      action={<TrendingUp className="h-5 w-5 text-brand" />}
    >
      {trendSeries.length === 0 ? (
        <EmptyState message="No trend data available." />
      ) : (
        <div className="space-y-3">
          {trendSeries.map((entry) => (
            <div key={entry.label} className="flex items-center justify-between text-sm text-gray-700">
              <span className="font-medium text-gray-900">{entry.label}</span>
              <div className="flex items-center gap-4">
                <span>Assigned: {formatIndianNumber(entry.assigned)}</span>
                <span>Completed: {formatIndianNumber(entry.completed)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );

  const renderCoursePerformance = () => (
    <SectionCard
      title="Content Engagement"
      subtitle="Top content by participation"
      action={<PieChart className="h-5 w-5 text-brand" />}
    >
      {coursePerformance.length === 0 ? (
        <EmptyState message="No content performance data." />
      ) : (
        <div className="space-y-4">
          {coursePerformance
            .filter((course) => {
              const title = (course.title || '').toLowerCase();
              const category = (course.category || '').toLowerCase();
              return category !== 'post-assessment' && !title.includes('quiz');
            })
            .map((course) => (
              <div key={course.id} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{course.title}</p>
                  <p className="text-xs text-gray-500">{course.category}</p>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>{formatIndianNumber(course.learners)} learners</span>
                  <span>{course.completionRate}% complete</span>
                </div>
              </div>
            ))}
        </div>
      )}
    </SectionCard>
  );

  const renderQuizPerformance = () => {
    const stats = analytics?.quiz;
    return (
      <SectionCard title="Assessment Performance" subtitle="Attempts & pass rates">
        {!stats ? (
          <EmptyState message="No assessment analytics available." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">{stats.avgScore ?? 0}%</p>
              <p className="text-xs text-gray-500 mt-1">Average Score</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">{stats.passRate ?? 0}%</p>
              <p className="text-xs text-gray-500 mt-1">Pass Rate</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-semibold text-gray-900">{formatIndianNumber(stats.attempts ?? 0)}</p>
              <p className="text-xs text-gray-500 mt-1">Attempts</p>
            </div>
          </div>
        )}
      </SectionCard>
    );
  };

  const renderSessionInsights = () => {
    const sessionsThisWeek = analytics?.kpis?.sessionsThisWeek || 0;
    const upcomingCount = analytics?.upcomingSessions?.length || 0;
    return (
      <SectionCard title="Class Schedule Insights">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">{formatIndianNumber(sessionsThisWeek)}</p>
            <p className="text-xs text-gray-500 mt-1">Classes this week</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">{formatIndianNumber(upcomingCount)}</p>
            <p className="text-xs text-gray-500 mt-1">Upcoming classes</p>
          </div>
        </div>
      </SectionCard>
    );
  };

  const renderDepartmentHealth = () => (
    <SectionCard title="Department Learning Status" subtitle="Assignment completion by department">
      {departmentPerformance.length === 0 ? (
        <EmptyState message="No department statistics available." />
      ) : (
        <div className="space-y-4">
          {departmentPerformance.map((dept) => (
            <div key={dept.department}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-900">{dept.department}</span>
                <span className="text-gray-600">{dept.completionRate}%</span>
              </div>
              <div className="mt-2 h-2 bg-gray-100 rounded-full">
                <div className="h-2 bg-brand rounded-full" style={{ width: `${dept.completionRate}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );


  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Learning Analytics</h1>
          <p className="text-gray-600 mt-1">End-to-end visibility into learning delivery</p>
        </div>
        <Button variant="outline" icon={<Download className="h-4 w-4" />} onClick={handleExportAnalytics}>
          Export
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          Loading analytics...
        </div>
      ) : (
        <>
          <div className="space-y-6">
            {renderSummary()}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {renderTrend()}
              {renderCoursePerformance()}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {renderQuizPerformance()}
              {renderSessionInsights()}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="space-y-6 lg:col-span-2">{renderAtRiskLearners()}</div>
              <div className="space-y-6">
                {renderUpcomingSessions()}
                {renderDepartmentHealth()}
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
};

export default ReportsAnalytics;
